import Switch from '@/components/FormElements/Switch';
import TextField from '@/components/FormElements/TextField';
import { HOSTNAME } from '@/configs/dybr';
import css from '@/scenes/User/Settings/SettingsCommon.module.css';
import Select from '@/components/FormElements/Select';
import { useEffect, useState } from 'react';

import {
  Block,
  ContentWrapper,
  Settings,
  SettingSectionTitle,
  SettingsFlexbox
} from '@/components/DybrComponents/StyledServicePagesElements';
import {
  useActiveProfile,
  useTokenState
} from '@/store/localStorage/useTokenState';
import { selectActiveProfileSettingsAugmented } from '@/store/user/selectors/selectActiveProfileSettingsAugmented';
import { updateProfileSetting } from '@/store/user/thunks/updateProfileSetting';
import { useDispatch, useSelector } from 'react-redux';
import { MAX_ENTRY_CONTENT_HEIGHT } from '../../../components/Shared/constants';
import { ActionButton } from '@/components/Shared/Buttons';
import RadioGroup from '@/components/FormElements/RadioGroup';
import { bannedCommentsOptions } from '@/utils/helpers/settingsHelpers';

export function CommonSettings() {
  const dispatch = useDispatch();
  const { activeProfileId } = useActiveProfile();
  const settings = useSelector(state =>
    selectActiveProfileSettingsAugmented(state, { activeProfileId })
  );

  useEffect(() => {
    document.title = `Общие настройки`;
  }, []);

  const [newComments, setNewComments] = useState(
    settings.notifications.comments.enable
  );
  const [newPosts, setNewPosts] = useState(
    settings.notifications.entries.enable
  );
  const [newMessages, setNewMessages] = useState(
    settings.notifications.messages.enable
  );
  const [newMentions, setNewMentions] = useState(
    settings.notifications.mentions.enable
  );
  const [quickLinkEnable, setQuickLinkEnable] = useState(
    settings.quickAccessLink.enable
  );
  const [quickLinkName, setQuickLinkName] = useState(
    settings.quickAccessLink.name
  );
  const [quickLinkUrl, setQuickLinkUrl] = useState(
    settings.quickAccessLink.url
  );
  const [autocutLength, setAutocutLength] = useState(
    settings.feed.autocutLength
      ? settings.feed.autocutLength
      : MAX_ENTRY_CONTENT_HEIGHT
  );
  const [autocutEnabled, setAutocutEnabled] = useState(
    autocutLength ? autocutLength > 0 : true
  );
  const [showBannedComments, setShowBannedComments] = useState(
    settings.bannedComments.show
  );
  const [token] = useTokenState();

  const updateBannedCommentsConfig = value => {
    setShowBannedComments(value);
    updateProfileSetting('bannedComments.show', value, token);
  };

  return (
    <ContentWrapper>
      <Block>
        <SettingsFlexbox>
          <SettingSectionTitle>
            <h4>Почтовые уведомления</h4>
            <p>О чём бы вы хотели получать уведомления на почту?</p>
          </SettingSectionTitle>
          <Settings>
            <Switch
              label="новые комментарии"
              checked={newComments}
              onClick={e => {
                setNewComments(!newComments);
                dispatch(
                  updateProfileSetting(
                    'notifications.comments',
                    { enable: e.target.checked, regularity: 'timely' },
                    token
                  )
                );
              }}
            />
            <Switch
              label="новые посты в избранном"
              checked={newPosts}
              onClick={e => {
                setNewPosts(!newPosts);
                dispatch(
                  updateProfileSetting(
                    'notifications.entries',
                    { enable: e.target.checked, regularity: 'timely' },
                    token
                  )
                );
              }}
            />
            <Switch
              label="новые личные сообщения"
              checked={newMessages}
              onClick={e => {
                setNewMessages(!newMessages);
                dispatch(
                  updateProfileSetting(
                    'notifications.messages',
                    { enable: e.target.checked, regularity: 'timely' },
                    token
                  )
                );
              }}
            />
            <Switch
              label="новые упоминания"
              checked={newMentions}
              onClick={e => {
                setNewMentions(!newMentions);
                dispatch(
                  updateProfileSetting(
                    'notifications.mentions',
                    { enable: e.target.checked, regularity: 'timely' },
                    token
                  )
                );
              }}
            />
          </Settings>
        </SettingsFlexbox>
      </Block>
      <Block>
        <SettingsFlexbox>
          <SettingSectionTitle>
            <h4>Кнопка быстрого доступа</h4>
            <p>
              Вы можете добавить свою личную кнопку в верхнюю панель, которая
              будет вести куда угодно на сайте. Например, можно сделать ссылку
              на общую ленту.
            </p>
          </SettingSectionTitle>
          <Settings>
            <Switch
              label="использовать быструю кнопку"
              checked={quickLinkEnable}
              onClick={e => {
                setQuickLinkEnable(!quickLinkEnable);
                dispatch(
                  updateProfileSetting(
                    'quickAccessLink.enable',
                    e.target.checked,
                    token
                  )
                );
              }}
            />
            <TextField
              label="название кнопки"
              value={quickLinkName}
              onChange={e => {
                setQuickLinkName(e.target.value);
              }}
              onBlur={e => {
                dispatch(
                  updateProfileSetting(
                    'quickAccessLink.name',
                    e.target.value,
                    token
                  )
                );
              }}
              touched={true}
            />
            <TextField
              className={css.topMargin}
              prefix={HOSTNAME + '/'}
              label="адрес страницы"
              value={quickLinkUrl}
              onChange={e => {
                setQuickLinkUrl(e.target.value);
              }}
              onBlur={e => {
                dispatch(
                  updateProfileSetting(
                    'quickAccessLink.url',
                    e.target.value,
                    token
                  )
                );
              }}
              touched={true}
            />
          </Settings>
        </SettingsFlexbox>
      </Block>
      <Block>
        <SettingsFlexbox>
          <SettingSectionTitle>
            <h4>Автоматическое ограничение длинных постов</h4>
            <p>
              Показывает только небольшую часть записи. По кнопке “Показать
              полностью” можно раскрыть запись и прочитать целиком.
            </p>
          </SettingSectionTitle>
          <Settings>
            <Switch
              label="использовать автокат в общей ленте"
              checked={autocutEnabled}
              onClick={e => {
                setAutocutEnabled(!autocutEnabled);
                const newAutocutLength = autocutEnabled
                  ? -1
                  : MAX_ENTRY_CONTENT_HEIGHT;
                setAutocutLength(newAutocutLength);
                dispatch(
                  updateProfileSetting(
                    'feed.autocutLength',
                    newAutocutLength,
                    token
                  )
                );
              }}
            />
          </Settings>
        </SettingsFlexbox>
      </Block>

      <Block>
        <SettingsFlexbox>
          <SettingSectionTitle>
            <h4>Заблокированные пользователи</h4>
            <p>
              Заблокированные пользователи не могут писать в вашем блоге. Вы
              также можете выбрать, скрывать ли их комментарии.
            </p>
          </SettingSectionTitle>
          <Settings>
            <RadioGroup
              name="myBlogBannedComments"
              title="комментарии заблокированных"
              checked={showBannedComments}
              onCheck={updateBannedCommentsConfig}
              items={bannedCommentsOptions}
            />
          </Settings>
        </SettingsFlexbox>
      </Block>
    </ContentWrapper>
  );
}
